import QRCode from "qrcode";
import { businessConfig } from "./generated/business";
import { icon, type IconName } from "./icons";
import { downloadVCard } from "./vcard";
import "./styles.css";

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("Application root was not found");
}

const configuredCardUrl = import.meta.env.VITE_PUBLIC_CARD_URL?.trim();
const publicCardUrl =
  configuredCardUrl || new URL("/", window.location.href).toString();
const walletPassUrl =
  import.meta.env.VITE_WALLET_PASS_URL?.trim() || "/api/pass";
const walletEnabled = import.meta.env.VITE_WALLET_ENABLED === "true";

const contactIcons: Record<
  (typeof businessConfig.contact_order)[number],
  IconName
> = {
  whatsapp: "whatsapp",
  telephone: "phone",
  email: "email",
  website: "website",
};

const contactLabels: Record<
  (typeof businessConfig.contact_order)[number],
  string
> = {
  whatsapp: "WhatsApp",
  telephone: "Telephone",
  email: "Email",
  website: "Website",
};

const serviceIcons: IconName[] = ["shield", "diagnostics", "tools", "mobile"];

const walletMarkup = walletEnabled
  ? `<a class="wallet-button" href="${walletPassUrl}">
      ${icon("wallet")}
      <span>Add to Apple Wallet</span>
    </a>`
  : `<div class="wallet-status" role="status" aria-label="Apple Wallet setup in progress">
      ${icon("wallet")}
      <span><strong>Apple Wallet</strong><small>Setup in progress</small></span>
    </div>`;

function contactDisplayMarkup(
  key: (typeof businessConfig.contact_order)[number],
  display: string,
): string {
  if (key === "email") {
    return display
      .replace("@", "@<wbr>")
      .replace("greenwood", "greenwood<wbr>");
  }
  if (key === "website") {
    return display
      .replace("www.", "www.<wbr>")
      .replace("greenwood", "greenwood<wbr>");
  }
  return display;
}

function contactMarkup(
  key: (typeof businessConfig.contact_order)[number],
): string {
  const contact = businessConfig.contacts[key];
  return `<a class="contact-action contact-action--${key}" href="${contact.url}" aria-label="${contactLabels[key]} ${contact.display}">
    <span class="contact-action__icon">${icon(contactIcons[key])}</span>
    <span class="contact-action__copy">
      <span class="contact-action__label">${contactLabels[key]}</span>
      <span class="contact-action__value">${contactDisplayMarkup(key, contact.display)}</span>
    </span>
    <span class="contact-action__arrow" aria-hidden="true">›</span>
  </a>`;
}

app.innerHTML = `
  <main id="main-content" class="page-shell">
    <section class="digital-card" aria-labelledby="card-title">
      <div class="digital-card__glow" aria-hidden="true"></div>
      <header class="brand-header">
        <img
          class="brand-logo"
          src="/generated/greenwood-logo.png"
          alt="Greenwood Autocare"
          width="1774"
          height="887"
        />
        <h1 id="card-title" class="visually-hidden">Greenwood Autocare digital business card</h1>
        <p class="brand-kicker">Reliable &amp; Professional</p>
      </header>

      <div class="contact-list" aria-label="Contact Greenwood Autocare">
        ${businessConfig.contact_order.map(contactMarkup).join("")}
      </div>

      <div class="primary-actions">
        ${walletMarkup}
        <button class="secondary-button" id="save-contact" type="button">
          ${icon("download")}
          <span>Save contact</span>
        </button>
        <button class="secondary-button" id="share-card" type="button">
          ${icon("share")}
          <span>Share card</span>
        </button>
      </div>
    </section>

    <section class="details-card" aria-labelledby="services-title">
      <div class="section-heading">
        <p class="section-heading__eyebrow">Greenwood Autocare</p>
        <h2 id="services-title">Service where you need it</h2>
      </div>
      <ul class="service-list">
        ${businessConfig.service_messages
          .map(
            (service, index) => `<li>
              <span class="service-list__icon">${icon(serviceIcons[index] ?? "tools")}</span>
              <span>${service}</span>
            </li>`,
          )
          .join("")}
      </ul>
      <p class="coverage">${icon("location")}<span>${businessConfig.coverage.summary}</span></p>
    </section>

    <section class="trust-strip" aria-label="Why choose Greenwood Autocare">
      ${businessConfig.trust_messages
        .map(
          (message) => `<div class="trust-item">
            ${icon("shield")}
            <span>${message}</span>
          </div>`,
        )
        .join("")}
    </section>

    <footer class="page-footer">
      <span>© ${new Date().getFullYear()} ${businessConfig.business.name}</span>
      <a href="${businessConfig.contacts.website.url}">${businessConfig.contacts.website.display}</a>
    </footer>
  </main>

  <dialog class="share-dialog" id="share-dialog" aria-labelledby="share-title">
    <button class="dialog-close" id="close-share" type="button" aria-label="Close share card">${icon("close")}</button>
    <div class="share-dialog__mark">
      <img src="/generated/greenwood-mark.png" alt="" width="1254" height="1254" />
    </div>
    <p class="section-heading__eyebrow">Greenwood Autocare</p>
    <h2 id="share-title">Scan to share</h2>
    <p>Open this digital card on another phone.</p>
    <div class="qr-frame"><canvas id="share-qr" aria-label="QR code linking to the Greenwood Autocare digital card"></canvas></div>
    <a class="share-url" href="${publicCardUrl}">${new URL(publicCardUrl).host}</a>
  </dialog>
`;

const saveContactButton =
  document.querySelector<HTMLButtonElement>("#save-contact");
const shareButton = document.querySelector<HTMLButtonElement>("#share-card");
const shareDialog = document.querySelector<HTMLDialogElement>("#share-dialog");
const closeShareButton =
  document.querySelector<HTMLButtonElement>("#close-share");
const qrCanvas = document.querySelector<HTMLCanvasElement>("#share-qr");

saveContactButton?.addEventListener("click", () =>
  downloadVCard(businessConfig),
);

let qrReady = false;

shareButton?.addEventListener("click", async () => {
  if (!shareDialog || !qrCanvas) return;
  if (!qrReady) {
    await QRCode.toCanvas(qrCanvas, publicCardUrl, {
      errorCorrectionLevel: "M",
      margin: 4,
      width: 256,
      color: { dark: "#073f26", light: "#ffffff" },
    });
    qrReady = true;
  }
  shareDialog.showModal();
});

closeShareButton?.addEventListener("click", () => shareDialog?.close());

shareDialog?.addEventListener("click", (event) => {
  if (event.target === shareDialog) shareDialog.close();
});
